// Wedding Photo Loader with ALL 1,184 Real Cloudflare Images
// This utility handles all the real wedding photos uploaded to Cloudflare Images

import allRealPhotos from '../data/allRealPhotos.js';

/**
 * Generate a photo object with metadata
 */
const generatePhoto = (basePhoto, index) => {
  return {
    id: basePhoto.id, // Real Cloudflare Image ID
    filename: basePhoto.filename, // Original filename
    type: 'image',
    title: `Wedding Memory ${index + 1}`,
    description: `Beautiful moment from Fuad & Donika's wedding - ${basePhoto.filename}`
  };
};

/**
 * Get total number of available photos
 */
export const getTotalPhotoCount = () => {
  return allRealPhotos.length;
};

/**
 * Generate all photos
 * @returns {Array} Array of all photo objects
 */
export const generateAllPhotos = () => {
  return allRealPhotos.map((photo, index) => generatePhoto(photo, index));
};

/**
 * Get photos in batches for performance
 * @param {number} page - Page number (0-based)
 * @param {number} pageSize - Number of photos per page
 * @returns {Object} { photos: Array, hasMore: boolean, total: number }
 */
export const getPhotosBatch = (page = 0, pageSize = 100) => {
  const totalCount = allRealPhotos.length;
  const startIndex = page * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);
  
  const photos = allRealPhotos
    .slice(startIndex, endIndex)
    .map((photo, index) => generatePhoto(photo, startIndex + index));
  
  return {
    photos,
    hasMore: endIndex < totalCount,
    total: totalCount,
    currentPage: page,
    totalPages: Math.ceil(totalCount / pageSize)
  };
};

/**
 * Search photos by filename or title
 * @param {string} searchTerm - Search term
 * @returns {Array} Matching photos
 */
export const searchPhotos = (searchTerm) => {
  if (!searchTerm) return [];
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  const results = [];
  
  allRealPhotos.forEach((photo, index) => {
    if (results.length >= 100) return; // Limit results
    
    if (
      photo.filename.toLowerCase().includes(lowerSearchTerm) ||
      `Wedding Memory ${index + 1}`.toLowerCase().includes(lowerSearchTerm)
    ) {
      results.push(generatePhoto(photo, index));
    }
  });
  
  return results;
};

/**
 * Get random photos
 * @param {number} count - Number of random photos to get
 * @returns {Array} Random photos
 */
export const getRandomPhotos = (count = 24) => {
  const totalCount = allRealPhotos.length;
  const photos = [];
  const usedIndices = new Set();
  
  while (photos.length < count && usedIndices.size < totalCount) {
    const randomIndex = Math.floor(Math.random() * totalCount);
    
    if (!usedIndices.has(randomIndex)) {
      usedIndices.add(randomIndex);
      const photo = allRealPhotos[randomIndex];
      photos.push(generatePhoto(photo, randomIndex));
    }
  }
  
  return photos;
};

/**
 * Configuration object
 */
export const PHOTO_CONFIG = {
  TOTAL_PHOTOS: allRealPhotos.length, // All 1184 real photos
  DEFAULT_PAGE_SIZE: 50,
  MAX_SEARCH_RESULTS: 100,
  RANDOM_SAMPLE_SIZE: 24
};
